import { useCallback, useMemo } from 'react';

import AwsS3 from '@uppy/aws-s3';
import type { Body, Meta, Uppy as UppyType } from '@uppy/core';
import Uppy from '@uppy/core';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionActionsPageData } from '#src/app/pages/actions/pageContext';
import { getPhotoUploadCredentials } from '#src/app/shared/api/photoUpload';

import { useVideosComponentContext } from '../../../../../context';
import type { Payload } from '../types';

// eslint-disable-next-line max-lines-per-function
export function useUpload() {
	const utilityContext = useUtilityContext();
	const {
		data: {
			featureConfigs: { campaignSupporterVoicesSettings },
		},
	} = usePetitionActionsPageData();

	const maxImageSize = campaignSupporterVoicesSettings.maxImageSize || 5;
	const maxVideoSize = campaignSupporterVoicesSettings.maxVideoSize || 20;
	const minVideoSize = campaignSupporterVoicesSettings.minVideoSize || 0.04;

	const {
		data: { videoSource },
	} = useVideosComponentContext();

	/**
	 * For backend file size restrictions, see:
	 * https://github.com/change/corgi/blob/main/apps/middle-end/src/gql/types/UploadCredentials/resolvers/uploadCredentialsResolver.ts#L0-L1
	 */
	const MEGABYTE = 1024 ** 2;
	const MAX_SIZE_IMAGE = maxImageSize * MEGABYTE;
	const MAX_SIZE_VIDEO = maxVideoSize * MEGABYTE;
	const MIN_SIZE_VIDEO = minVideoSize * MEGABYTE;

	const uppyForVideo = useMemo(
		() =>
			new Uppy<Meta, Body>({
				id: 'campaign-actions-videos',
				restrictions: {
					maxNumberOfFiles: 1,
					allowedFileTypes: ['video/*'],
					maxFileSize: MAX_SIZE_VIDEO,
					minFileSize: MIN_SIZE_VIDEO,
				},
				autoProceed: true,
				meta: {
					video_source: videoSource,
				},
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);
	const uppyForImage = useMemo(
		() =>
			new Uppy<Meta, Body>({
				id: 'campaign-actions-images',
				restrictions: {
					maxNumberOfFiles: 1,
					allowedFileTypes: ['image/*'],
					maxFileSize: MAX_SIZE_IMAGE,
				},
				autoProceed: false,
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	const uploadWithUppy = useCallback(
		async (body: Payload, uppy: UppyType<Meta, Body>) => {
			const credentials = await getPhotoUploadCredentials(body.filename, utilityContext, 'USER_UPLOADS');
			if (credentials) {
				uppy.cancelAll();
				uppy.use(AwsS3, {
					id: Math.random().toString().split('.')[1],
					shouldUseMultipart: false,
					getUploadParameters: () => ({
						method: 'POST',
						url: credentials.postUrl,
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						fields: credentials.s3formData as Record<string, string>,
					}),
				});
				uppy.addFile({
					name: body.filename,
					type: body.type,
					data: body.blob,
				});
				return uppy.upload();
			}

			throw new Error('upload failed (preflight)');
		},
		[utilityContext],
	);

	const upload = useCallback(
		async (body: Payload) => {
			const uppy = body.type.startsWith('image') ? uppyForImage : uppyForVideo;
			const result = await uploadWithUppy(body, uppy);

			if (!result) {
				// Shouldn't be possible, but has been seen in the wild:
				// https://changeorg.airbrake.io/projects/257900/groups/3649360736292359528?dur=90d%2F1d&last_n=7&filters=%7B%22order%22%3A%22last_notice%22%2C%22resolved%22%3A%22false%22%2C%22context.eventName%22%3A%228301508253640944765%22%7D&tab=overview
				throw new Error('Upload failed: Uppy returned undefined');
			}

			if (result.failed?.length) {
				// Try to get more detail of what went wrong. Errors like this weren't actionable:
				// https://changeorg.airbrake.io/projects/257900/groups/3637399076329670903/notices/3660043544113543873?dur=90d%2F1d&last_n=7&filters=%7B%22order%22%3A%22last_notice%22%2C%22resolved%22%3A%22false%22%2C%22context.eventName%22%3A%228301508253640944765%22%7D&tab=notice-detail
				const fileError = result.failed[0]?.error;
				throw new Error(`Upload failed: ${fileError}`);
			}

			const successUrl = result.successful?.[0]?.uploadURL;
			if (!successUrl) {
				// This case probably doesn't happen. Uppy should have returned a "failed" entry which would be caught above.
				throw new Error('Upload failed: no success upload URL');
			}

			return successUrl;
		},
		[uppyForImage, uppyForVideo, uploadWithUppy],
	);

	return { upload };
}
